import type { TvChannel } from '@setplex/tria-api'
import type { TvChannelPageMeta } from '~/entities/epg/channels'

import { attach, createEvent, sample } from 'effector'
import { CategoryId } from '~/entities/epg/categories'
import { epgChannelsModel } from '~/entities/epg/channels'
import { model as favoritesModel } from '~/features/favorites'
import { aye, concatById, idEq, nay } from '~/shared/helpers'
import { RELOAD_PAGE_COUNT } from './index.h'

// update channel favorite status, accepts channel's id and *current* favorite status
export const update = createEvent<{
  id: number
  favorite: boolean
  name?: string
}>()

// update channel favorite status effect, accepts channel's id and *new* favorite status
export const updateFx = attach({
  effect: favoritesModel.favoriteTvChannelFx,
})

// events after channel is liked or unliked
export const liked = createEvent<number>()
export const unliked = createEvent<number>()

// trigger update effect by request from UI
sample({
  clock: update,
  fn: ({ favorite, id, name }) => ({ favorite: !favorite, id, name }),
  target: updateFx,
})

// on successful "like" update, trigger liked event
sample({
  clock: updateFx.done,
  filter: ({ params }) => aye(params.favorite),
  fn: ({ params }) => params.id,
  target: liked,
})

// on successful "unlike" update, trigger unliked event
sample({
  clock: updateFx.done,
  filter: ({ params }) => nay(params.favorite),
  fn: ({ params }) => params.id,
  target: unliked,
})

//
// update "Favorites" category on channel like/unlike
//

export const add = createEvent<TvChannel | undefined>()
export const remove = createEvent<number>()
export const reload = createEvent()
export const dirty = createEvent()
export const clear = createEvent()

// reload all favorite channels list request
export const reloadFavoritesIfNeeded = createEvent()

// currently loaded favorite channels
export const $favorites = epgChannelsModel.$byCategory //
  .map(([all]) => all.get(CategoryId.Favorite) ?? [])

// currently loaded favorite channels meta
export const $meta = epgChannelsModel.$byCategory //
  .map(([, meta]) => meta.get(CategoryId.Favorite) ?? null)

// reload ALL favorite channels list effect
export const reloadFavoritesFx = attach({
  mapParams: (count: number) => ({
    'byCategoryId': CategoryId.Favorite,
    'only-favorites': true,
    'page': 0,
    'count': count,
  }),
  effect: epgChannelsModel.loadPageFx,
})

// when liked, in case there are no more favorites to load - just add new channel to list
sample({
  clock: liked,
  source: {
    all: epgChannelsModel.$all,
    favorites: $favorites,
    meta: $meta,
  },
  filter: ({ favorites, meta }, id) =>
    meta != null &&
    meta.isLast && // no more favorites to load
    favorites.find(idEq(id)) == null, // channel is not in favorites list yet
  fn: ({ all: [channels] }, id) => channels.get(id),
  target: add,
})

// when unliked, in case there are no more favorites to load - just remove channel from list
sample({
  clock: unliked,
  source: {
    all: epgChannelsModel.$all,
    meta: $meta,
  },
  filter: ({ meta }) => meta != null && meta.isLast,
  fn: (_, id) => id,
  target: remove,
})

// in case there ARE more favorites to load - just reload all of them,
// because I don't know what to do with liked/unliked channel locally
sample({
  clock: [liked, unliked],
  source: {
    meta: $meta,
  },
  filter: ({ meta }) => meta == null || meta.hasMore,
  target: reload,
})

// actually add channel to favorites list
// this is relatively safe, because there are no more favorites to load (hopefully)
sample({
  clock: add,
  source: epgChannelsModel.$byCategory,
  filter: (_, channel) => channel != null, // should not happen
  fn: ([channels, meta], channel) => {
    const categoryId = CategoryId.Favorite
    const existing = channels.get(categoryId) ?? []

    channels.set(categoryId, concatById(existing)([channel!]))
    return [channels, meta] as const
  },
  target: epgChannelsModel.$byCategory,
})

// mark favorites list as "dirty",
// instead of actually removing channel from it
// or instead of reloading all favorites list right away
// "dirty" state will be used to reload favorites list on active category change
sample({
  clock: [remove, reload, dirty],
  source: epgChannelsModel.$byCategory,
  fn: ([channels, meta]) => {
    const categoryId = CategoryId.Favorite
    const favoritesMeta = meta.get(categoryId) ?? ({} as TvChannelPageMeta)
    meta.set(categoryId, { ...favoritesMeta, dirty: true })
    return [channels, meta] as const
  },
  target: epgChannelsModel.$byCategory,
})

// reload favorites list on active category change,
// if favorites list is marked as "dirty"
sample({
  clock: reloadFavoritesIfNeeded,
  source: {
    favorites: $favorites,
    meta: $meta,
  },
  filter: ({ meta }) => aye(meta?.dirty),
  fn: ({ favorites }) => Math.max(favorites.length, RELOAD_PAGE_COUNT),
  target: reloadFavoritesFx,
})

// reset "dirty" state
sample({
  clock: reloadFavoritesFx,
  source: epgChannelsModel.$byCategory,
  fn: ([channels, meta]) => {
    const categoryId = CategoryId.Favorite
    const favoritesMeta = meta.get(categoryId) ?? ({} as TvChannelPageMeta)
    meta.set(categoryId, { ...favoritesMeta, dirty: false })
    channels.set(categoryId, []) // clear all favorites, because loading new ones doesn't do that
    return [channels, meta] as const
  },
  target: epgChannelsModel.$byCategory,
})

// clear favorites list
sample({
  clock: clear,
  source: epgChannelsModel.$byCategory,
  fn: ([channels, meta]) => {
    const categoryId = CategoryId.Favorite
    channels.delete(categoryId)
    meta.delete(categoryId)
    return [channels, meta] as const
  },
  target: epgChannelsModel.$byCategory,
})
